import * as i0 from '@angular/core';
import { EventEmitter, Directive, Input, Output, HostListener, Component, ContentChild, NgModule } from '@angular/core';
import { Subject, throttleTime } from 'rxjs';
import { JsonPipe, NgIf } from '@angular/common';

const INERTIAL_MOVE_DIVIDER = 15;
class Inertia {
    constructor(scrollDragDirective) {
        this.scrollDragDirective = scrollDragDirective;
        this.impulseY = 0;
        this.totalImpulseEnergy = 0;
        this.inertialMoveDivider = INERTIAL_MOVE_DIVIDER;
        this.mouseWayPoints = [];
        this.maxPointsToCheckSpeed = 3;
    }
    addMouseWayPoints(x, y) {
        if (this.mouseWayPoints.push({ x, y }) > this.maxPointsToCheckSpeed) {
            this.mouseWayPoints.shift();
        }
        return this;
    }
    stopInertion() {
        this.totalImpulseEnergy = 0;
        return this;
    }
    startInertialMove(x, y) {
        const totalDist = this.mouseWayPoints.reduce(function (prev, curr) {
            prev += curr.y;
            return prev;
        }, 0);
        this.impulseY = totalDist / this.mouseWayPoints.length - y;
        this.totalImpulseEnergy = Math.abs(this.impulseY);
        this.clearWayPoints();
        if (this.totalImpulseEnergy > 10) {
            this.inertialDisplacement();
        }
    }
    clearWayPoints() {
        this.mouseWayPoints.length = 0;
        return this;
    }
    inertialDisplacement() {
        if (this.totalImpulseEnergy <= 0) {
            this.inertialMoveDivider = INERTIAL_MOVE_DIVIDER;
            return;
        }
        const scrollOffset = this.impulseY;
        this.scrollDragDirective.scroll(scrollOffset);
        setTimeout(() => {
            this.totalImpulseEnergy -= Math.abs(scrollOffset / this.inertialMoveDivider);
            this.inertialDisplacement();
        }, 1000 / 60);
    }
}

var Direction;
(function (Direction) {
    Direction["Top"] = "top";
    Direction["Bottom"] = "bottom";
    Direction["Right"] = "right";
    Direction["Left"] = "left";
})(Direction || (Direction = {}));
class ScrollDragDirective {
    constructor(elemRef, renderer) {
        this.elemRef = elemRef;
        this.renderer = renderer;
        this.scrollDirection = 'Vertical';
        this.scrollShadow = false;
        this.shadowChange = new EventEmitter();
        this.backgroundColor = 'white';
        this.disableDrag = false;
        this.shadow = {
            [Direction.Top]: false,
            [Direction.Bottom]: false,
            [Direction.Right]: false,
            [Direction.Left]: false,
        };
        this.dateScrollChange$ = new Subject();
        this.scrollDragSnap = 0;
        this.isPressed = false;
        this.element = this.elemRef.nativeElement;
    }
    set ngxScrollDrag(value) {
        this.scrollDragSnap = Number(value || 0);
    }
    ngAfterViewInit() {
        this.inertia = new Inertia(this);
    }
    startDrag(ev) {
        if (this.disableDrag)
            return;
        if (this.scrollDirection === 'Vertical') {
            this.startDragY = ev.clientY + this.element.scrollTop;
        }
        else {
            this.startDragX = ev.clientX + this.element.scrollLeft;
        }
        this.isPressed = true;
        this.inertia.stopInertion();
    }
    drag(ev) {
        if (this.disableDrag)
            return;
        this.isPressed = ev.buttons === 1;
        if (this.isPressed) {
            if (this.scrollDirection === 'Vertical') {
                this.element.scrollTop = (this.startDragY ? this.startDragY - ev.clientY : 0);
            }
            else {
                this.element.scrollLeft = (this.startDragX ? this.startDragX - ev.clientX : 0);
            }
            this.inertia.addMouseWayPoints(ev.clientX, ev.clientY);
        }
    }
    leave(ev) {
        if (this.disableDrag)
            return;
        if (this.isPressed) {
            this.inertia.startInertialMove(ev.clientX, ev.clientY);
        }
        this.onScroll();
    }
    stopDrag(ev) {
        if (this.disableDrag)
            return;
        if (this.isPressed) {
            this.isPressed = false;
            this.inertia.startInertialMove(ev.clientX, ev.clientY);
        }
        this.onScroll();
    }
    onScroll() {
        this.dateScrollChange$.next();
    }
    ngOnInit() {
        this.dateScrollChangeSubscription = this.dateScrollChange$.pipe(throttleTime(100, undefined, { leading: true, trailing: true })).subscribe(() => {
            if (!this.isPressed && this.scrollDragSnap) {
                const scrollTop = Math.round(this.element.scrollTop / this.scrollDragSnap) * this.scrollDragSnap;
                this.element.scrollTo({ top: scrollTop, behavior: 'smooth' });
            }
            this.setBoxShadowClass();
            if (this.scrollShadow) {
                this.setBackgroundShadow(this.element);
            }
            else {
                this.renderer.setStyle(this.element, 'background', null);
            }
        });
        this.onScroll();
    }
    scroll(scrollOffset) {
        const draggedElm = this.element;
        if (this.scrollDirection === 'Vertical') {
            draggedElm.scrollTop = draggedElm.scrollTop + scrollOffset;
        }
        else {
            draggedElm.scrollLeft = draggedElm.scrollLeft + scrollOffset;
        }
        this.onScroll();
    }
    ngOnDestroy() {
        this.dateScrollChangeSubscription?.unsubscribe();
    }
    setBoxShadowClass() {
        const edgeZoneSize = 5;
        if (this.scrollDirection === "Vertical") {
            this.switchClass(this.element.scrollTop + this.element.clientHeight < this.element.scrollHeight - edgeZoneSize, Direction.Bottom);
            this.switchClass(this.element.scrollTop > edgeZoneSize, Direction.Top);
        }
        else {
            this.switchClass(Math.abs(this.element.scrollLeft) > edgeZoneSize, Direction.Left);
            // in RTL chrome desktop gets scrollLeft as a negative number unlike chrome mobile
            this.switchClass(Math.abs(this.element.scrollLeft) + this.element.clientWidth < Math.abs(this.element.scrollWidth - edgeZoneSize), Direction.Right);
        }
        this.renderer.addClass(this.element, 'ngx-scroll-drag-shadow');
    }
    switchClass(condition, direction) {
        const shadowClass = 'ngx-scroll-drag-shadow-' + direction;
        this.shadow[direction] = condition;
        this.renderer[condition ? 'addClass' : 'removeClass'](this.element, shadowClass);
    }
    setBackgroundShadow(elm) {
        const shadowSize = '0.75em';
        const backgroundColor = this.backgroundColor;
        const shadowColor = 'rgba(34,34,34, 0.5)';
        const transparent = 'rgba(255, 255, 255, 0)';
        if (this.scrollDirection === 'Vertical') {
            this.renderer.setStyle(elm, 'background', `
        linear-gradient( ${backgroundColor} 30%, ${transparent} ) center top,
        linear-gradient( ${transparent}, ${backgroundColor} 70% ) center bottom,
        radial-gradient(farthest-side at 50% 0, ${shadowColor}, ${transparent}) center top,
        radial-gradient(farthest-side at 50% 100%, ${shadowColor}, ${transparent}) center bottom
    `);
            this.renderer.setStyle(elm, 'background-size', `100% calc(${shadowSize} * 4), 100% calc(${shadowSize} * 4), 100% ${shadowSize}, 100% ${shadowSize}`);
        }
        else {
            this.renderer.setStyle(elm, 'background', `
        linear-gradient(to right, ${backgroundColor}, ${backgroundColor}, ${transparent} calc(${shadowSize} * 2)),
        linear-gradient(to left, ${backgroundColor}, ${backgroundColor}, ${transparent} calc(${shadowSize} * 2)),
        radial-gradient(farthest-side at 0 50%, ${shadowColor}, ${transparent}),
        radial-gradient(farthest-side at 100% 50%, ${shadowColor}, ${transparent}) 100%
    `);
            this.renderer.setStyle(elm, 'background-size', `100% 100%, 100% 100%, ${shadowSize} 100%, ${shadowSize} 100%`);
        }
        this.renderer.setStyle(elm, 'background-repeat', 'no-repeat');
        this.renderer.setStyle(elm, 'background-color', this.backgroundColor);
        this.renderer.setStyle(elm, 'background-attachment', 'local, local, scroll, scroll');
    }
}
ScrollDragDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollDragDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
ScrollDragDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: ScrollDragDirective, selector: "[ngxScrollDrag]", inputs: { scrollDirection: "scrollDirection", scrollShadow: "scrollShadow", backgroundColor: "backgroundColor", disableDrag: "disableDrag", ngxScrollDrag: "ngxScrollDrag" }, outputs: { shadowChange: "shadowChange" }, host: { listeners: { "mousedown": "startDrag($event)", "mousemove": "drag($event)", "mouseleave": "leave($event)", "mouseup": "stopDrag($event)", "wheel": "onScroll($event)", "scroll": "onScroll($event)" }, properties: { "style.user-select": "\"none\"", "style.overflow-scrolling": "\"touch\"" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollDragDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ngxScrollDrag]',
                    host: {
                        '[style.user-select]': '"none"',
                        '[style.overflow-scrolling]': '"touch"',
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { scrollDirection: [{
                type: Input
            }], scrollShadow: [{
                type: Input
            }], shadowChange: [{
                type: Output
            }], backgroundColor: [{
                type: Input
            }], disableDrag: [{
                type: Input
            }], ngxScrollDrag: [{
                type: Input,
                args: ['ngxScrollDrag']
            }], startDrag: [{
                type: HostListener,
                args: ["mousedown", ["$event"]]
            }], drag: [{
                type: HostListener,
                args: ["mousemove", ["$event"]]
            }], leave: [{
                type: HostListener,
                args: ["mouseleave", ["$event"]]
            }], stopDrag: [{
                type: HostListener,
                args: ["mouseup", ["$event"]]
            }], onScroll: [{
                type: HostListener,
                args: ["wheel", ["$event"]]
            }, {
                type: HostListener,
                args: ["scroll", ["$event"]]
            }] } });

class ScrollShadowComponent {
    constructor() {
    }
    ngAfterContentInit() {
        this.shadow = this.scrollDragDirective?.shadow;
    }
}
ScrollShadowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollShadowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ScrollShadowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: ScrollShadowComponent, selector: "ngx-scroll-shadow", queries: [{ propertyName: "scrollDragDirective", first: true, predicate: ScrollDragDirective, descendants: true }], ngImport: i0, template: "<ng-content></ng-content>\n<div class=\"shadow-top\" [class.hide-shadow]=\"!shadow?.top\"></div>\n<div class=\"shadow-bottom\" [class.hide-shadow]=\"!shadow?.bottom\"></div>\n<div class=\"shadow-right\" [class.hide-shadow]=\"!shadow?.right\"></div>\n<div class=\"shadow-left\" [class.hide-shadow]=\"!shadow?.left\"></div>\n", styles: [":host{display:block;position:relative}div{position:absolute;pointer-events:none;z-index:1;transition:box-shadow 125ms ease-in-out}div.shadow-top{width:100%;box-shadow:inset 0 20px 16px -20px #00000080;height:20px;top:0;left:0}div.shadow-bottom{width:100%;box-shadow:inset 0 -20px 16px -20px #00000080;height:20px;bottom:0;left:0}div.shadow-left{height:100%;box-shadow:inset 20px 0 16px -20px #00000080;width:20px;left:0;top:0}div.shadow-right{height:100%;box-shadow:inset -20px 0 16px -20px #00000080;width:20px;right:0;top:0}div.hide-shadow{box-shadow:none}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: ScrollShadowComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-scroll-shadow', template: "<ng-content></ng-content>\n<div class=\"shadow-top\" [class.hide-shadow]=\"!shadow?.top\"></div>\n<div class=\"shadow-bottom\" [class.hide-shadow]=\"!shadow?.bottom\"></div>\n<div class=\"shadow-right\" [class.hide-shadow]=\"!shadow?.right\"></div>\n<div class=\"shadow-left\" [class.hide-shadow]=\"!shadow?.left\"></div>\n", styles: [":host{display:block;position:relative}div{position:absolute;pointer-events:none;z-index:1;transition:box-shadow 125ms ease-in-out}div.shadow-top{width:100%;box-shadow:inset 0 20px 16px -20px #00000080;height:20px;top:0;left:0}div.shadow-bottom{width:100%;box-shadow:inset 0 -20px 16px -20px #00000080;height:20px;bottom:0;left:0}div.shadow-left{height:100%;box-shadow:inset 20px 0 16px -20px #00000080;width:20px;left:0;top:0}div.shadow-right{height:100%;box-shadow:inset -20px 0 16px -20px #00000080;width:20px;right:0;top:0}div.hide-shadow{box-shadow:none}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { scrollDragDirective: [{
                type: ContentChild,
                args: [ScrollDragDirective]
            }] } });

class NgxScrollDragModule {
}
NgxScrollDragModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: NgxScrollDragModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxScrollDragModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: NgxScrollDragModule, declarations: [ScrollDragDirective,
        ScrollShadowComponent], imports: [JsonPipe,
        NgIf], exports: [ScrollDragDirective, ScrollShadowComponent] });
NgxScrollDragModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: NgxScrollDragModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: NgxScrollDragModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        ScrollDragDirective,
                        ScrollShadowComponent
                    ],
                    imports: [
                        JsonPipe,
                        NgIf
                    ],
                    exports: [ScrollDragDirective, ScrollShadowComponent]
                }]
        }] });

/*
 * Public API Surface of ngx-scroll-drag
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NgxScrollDragModule, ScrollDragDirective, ScrollShadowComponent };
